import _ from "lodash";
import {SENDER, STATUS} from "../../components/ai-assistant-chatbot/constants";
import {getActionExtras} from "../action-decorator/middlewareFactory";
import {REQUEST_SUCCESS, RequestMethod} from "../request/requestActions";
import {
  addAiAssistantChatbotEntry,
  AI_ASSISTANT_ANALYSIS_FETCH,
  AI_ASSISTANT_CHATBOT_CHAT_RESET,
  AI_ASSISTANT_CHATBOT_REQUEST_SEND,
  AI_ASSISTANT_CHATBOT_REQUEST_STOP,
  fetchAiAssistantAnalysis,
  resetAiAssistantChatbotChat,
  sendAiAssistantChatbotRequest,
  setAiAssistantChatbotLastQuery,
  setAiAssistantChatbotPackageId,
  updateAiAssistantChatbotMessage
} from "../../state/ai-assistant/aiAssistantAction";
import {cancelPendingRequests} from "../../state/pending-request/pendingRequestActions";
import {getIsInternalService} from "../../components/odata-viewer/utils";
import {getResourceFromCatalogById} from "../../utils/catalog";
import {getOdataDataQueryUrl, getOdataDataUrl, getOdataMetadataUrl} from "../../utils/odata";

const aiAssistantmiddleware =
  ({getState, dispatch}) =>
  next =>
  async action => {
    const {t} = getActionExtras(action);

    const state = getState();
    const transformedAction = _.cloneDeep(action);

    if (action.type === AI_ASSISTANT_CHATBOT_CHAT_RESET) {
      if (state?.pendingRequest?.length) {
        const requestToCancel = state.pendingRequest
          .filter(
            request =>
              request.label === AI_ASSISTANT_CHATBOT_REQUEST_SEND || request.label === AI_ASSISTANT_ANALYSIS_FETCH
          )
          .map(request => request.uuid);
        dispatch(cancelPendingRequests(requestToCancel));
      }
    } else if (action.type === AI_ASSISTANT_CHATBOT_REQUEST_STOP) {
      if (state?.pendingRequest?.length) {
        const requestToCancel = state.pendingRequest
          .filter(
            request =>
              request.label === AI_ASSISTANT_CHATBOT_REQUEST_SEND || request.label === AI_ASSISTANT_ANALYSIS_FETCH
          )
          .map(request => request.uuid);
        dispatch(cancelPendingRequests(requestToCancel));
      }
    } else if (action.type === REQUEST_SUCCESS) {
      if (action.payload.label === AI_ASSISTANT_CHATBOT_REQUEST_SEND) {
        const nodeId = state?.node?.nodeId;
        const catalog = state?.catalog;

        const odataBaseUrlFromConfig = state?.config?.externalServices?.odata;
        const aiBaseUrlFromConfig = state?.config?.externalServices?.ai;

        const aiAssistantState = state.aiAssistant;
        const {enabledFeatures} = aiAssistantState;

        const {message, isFollowUpMessage = false} = action.payload.extra;
        const {sessionId, data, results} = action.payload.response;
        const {packageIdentifier, resourceIdentifier, query} = data || {};

        if (data) {
          const addQuickResponseMessage = () => {
            dispatch(
              addAiAssistantChatbotEntry({
                message: t("modules.aiAssistant.chatbot.messages.continueOrRestart"),
                sender: SENDER.bot,
                status: STATUS.success,
                isQuickResponseMessage: true,
                quickResponses: [
                  {
                    label: t("modules.aiAssistant.chatbot.messages.continue"),
                    onClick: messageIdx => {
                      dispatch(setAiAssistantChatbotPackageId(packageIdentifier || null));
                      dispatch(
                        addAiAssistantChatbotEntry({
                          message: t("modules.aiAssistant.chatbot.messages.continue"),
                          sender: SENDER.user,
                          status: STATUS.success
                        })
                      );
                      dispatch(
                        updateAiAssistantChatbotMessage(messageIdx, {
                          isQuickResponseMessage: false,
                          quickResponses: null
                        })
                      );
                    }
                  },
                  {
                    label: t("modules.aiAssistant.chatbot.messages.restart"),
                    onClick: () => {
                      dispatch(resetAiAssistantChatbotChat());
                    }
                  }
                ]
              })
            );
          };

          try {
            if (data.isValid) {
              const resource = getResourceFromCatalogById(catalog, resourceIdentifier);

              if (resource) {
                const odataBaseurl = resource?.baseurl || odataBaseUrlFromConfig || null;

                const {datasource, mainentityset} = resource;

                transformedAction.payload.response.queryInfo = {
                  baseurl: odataBaseurl,
                  datasource: datasource,
                  mainentityset: mainentityset,
                  query: query
                };

                const metadataResponse = await fetch(getOdataMetadataUrl(odataBaseurl, datasource), {
                  method: RequestMethod.GET,
                  headers: {
                    Authorization:
                      !resource?.baseurl && odataBaseUrlFromConfig && state?.user?.token
                        ? `bearer ${state.user.token}`
                        : undefined
                  }
                });
                const metadataResponseParsed = await metadataResponse.json();
                const metadata = metadataResponseParsed?.ns?.[mainentityset];
                const isInternalService = getIsInternalService(metadata);

                const countResponse = await fetch(
                  isInternalService
                    ? getOdataDataUrl(odataBaseurl, datasource, mainentityset)
                    : getOdataDataQueryUrl(odataBaseurl, datasource, mainentityset, query, true),
                  {
                    method: isInternalService ? RequestMethod.POST : RequestMethod.GET,
                    headers: {
                      Authorization: !resource?.baseurl && state?.user?.token ? `bearer ${state.user.token}` : undefined
                    },
                    body: isInternalService && query && query.length ? `${query.substring(1)}&$count=true` : ""
                  }
                );

                const countResponseParsed = await countResponse.json();
                const count = countResponseParsed?.["@odata.count"];

                if (count === undefined || count === null) {
                  throw new Error();
                }

                // Store the last successful query in the state, to be used as context for follow-up messages
                dispatch(setAiAssistantChatbotLastQuery(query));

                const isCountQuery = (query || "").includes("$count=true");
                if (isCountQuery) {
                  transformedAction.payload.response.count = count;
                  transformedAction.payload.response.data = null;
                  if (isFollowUpMessage) {
                    addQuickResponseMessage();
                  }
                } else {
                  const handleStandardCase = () => {
                    transformedAction.payload.response.metadata = metadata;
                    transformedAction.payload.response.onDataFetch = (fetchedData, fetchedDataCount) => {
                      const isAnalyzeEnabled = enabledFeatures?.analyze || false;
                      const analyzeSize = state.appConfig?.aiAssistant?.analyzeSize;

                      if (isAnalyzeEnabled && analyzeSize) {
                        dispatch(
                          addAiAssistantChatbotEntry({
                            message: t("modules.aiAssistant.chatbot.chatDialog.messages.analysis"),
                            sender: SENDER.bot,
                            status: STATUS.pending,
                            hideAvatar: true
                          })
                        );

                        const dataToAnalyze = fetchedData.slice(0, analyzeSize);
                        const dataCount = fetchedDataCount ? fetchedDataCount : fetchedData.length;
                        dispatch(
                          fetchAiAssistantAnalysis(
                            nodeId,
                            packageIdentifier,
                            resourceIdentifier,
                            message,
                            sessionId,
                            dataToAnalyze,
                            dataCount > dataToAnalyze.length,
                            odataBaseUrlFromConfig,
                            aiBaseUrlFromConfig,
                            {
                              onLoadEnd: () => {
                                if (isFollowUpMessage) {
                                  addQuickResponseMessage();
                                }
                              }
                            }
                          )
                        );
                      } else {
                        if (isFollowUpMessage) {
                          addQuickResponseMessage();
                        }
                      }
                    };
                  };

                  if (count === 0) {
                    transformedAction.payload.response.data = null;
                    transformedAction.payload.response.onLoadEnd = () => {
                      dispatch(
                        addAiAssistantChatbotEntry({
                          message: t("modules.aiAssistant.chatbot.messages.emptyData"),
                          sender: SENDER.bot,
                          status: STATUS.loading,
                          onLoadEnd: () => {
                            if (isFollowUpMessage) {
                              addQuickResponseMessage();
                            }
                          }
                        })
                      );
                    };
                  } else if (count === 1) {
                    const dataResponse = await fetch(
                      isInternalService
                        ? getOdataDataUrl(odataBaseurl, datasource, mainentityset)
                        : getOdataDataQueryUrl(odataBaseurl, datasource, mainentityset, query),
                      {
                        method: isInternalService ? RequestMethod.POST : RequestMethod.GET,
                        headers: {
                          Authorization:
                            !resource?.baseurl && odataBaseUrlFromConfig && state?.user?.token
                              ? `bearer ${state.user.token}`
                              : undefined
                        },
                        body: isInternalService && query && query.length ? `${query.substring(1)}` : ""
                      }
                    );
                    if (!dataResponse.ok) throw new Error();
                    const dataResponseParsed = await dataResponse.json();
                    if ((dataResponseParsed?.value ?? []).length === 1) {
                      const row = dataResponseParsed.value[0];
                      const columns = Object.keys(row).filter(column => !column.includes("@odata.type"));
                      if (columns.length === 1) {
                        const value = row[columns[0]];
                        if (value !== null && value !== undefined) {
                          transformedAction.payload.response.count = value;
                        } else {
                          transformedAction.payload.response.count = null;
                          transformedAction.payload.response.response = t(
                            "modules.aiAssistant.chatbot.messages.invalidCount"
                          );
                        }
                        transformedAction.payload.response.data = null;
                        transformedAction.payload.response.onLoadEnd = () => {
                          if (isFollowUpMessage) {
                            addQuickResponseMessage();
                          }
                        };
                      } else {
                        handleStandardCase();
                      }
                    } else {
                      handleStandardCase();
                    }
                  } else {
                    handleStandardCase();
                  }
                }
              }
            } else {
              transformedAction.payload.response.response = t("modules.aiAssistant.chatbot.messages.invalidAiResponse");
              transformedAction.payload.response.data = null;
              transformedAction.payload.response.onLoadEnd = () => {
                if (isFollowUpMessage) {
                  addQuickResponseMessage();
                }
              };
            }
          } catch (error) {
            transformedAction.payload.response.response = t("modules.aiAssistant.chatbot.messages.invalidQuery");
            transformedAction.payload.response.data = null;
            transformedAction.payload.response.onLoadEnd = () => {
              if (isFollowUpMessage) {
                addQuickResponseMessage();
              }
            };
          }
        } else if (results) {
          const aiConfig = state.appConfig?.aiAssistant ?? {};
          const followUpMinRelevanceScore =
            typeof aiConfig.followUpMinRelevanceScore === "number" ? aiConfig.followUpMinRelevanceScore : 1.2;
          const relevantResult = results.find(
            res =>
              res.relevanceScore >= followUpMinRelevanceScore &&
              (res?.resourceIdentifiers || []).find(id => getResourceFromCatalogById(catalog, id)?.type === "odata")
          );

          if (relevantResult) {
            transformedAction.payload.response.onLoadEnd = () => {
              dispatch(
                sendAiAssistantChatbotRequest(
                  nodeId,
                  sessionId,
                  message,
                  relevantResult.identifier,
                  catalog,
                  odataBaseUrlFromConfig,
                  aiBaseUrlFromConfig,
                  undefined,
                  {
                    isFollowUpMessage: true,
                    followUpPendingMessage: t("modules.aiAssistant.chatbot.messages.followUpPending", {
                      title: relevantResult.title
                    })
                  }
                )
              );
            };
          }
        }
      }
    }

    return next(transformedAction);
  };

export default aiAssistantmiddleware;
